import React, {Fragment} from "react";
import {observer} from "mobx-react";
import {useViewController} from "data/services/locator";
import {IModalPromotedController} from "views/components/modals/modal_promoted/modal_promoted.controller";
import {Bindings} from "data/constants/bindings";
import {Button, IconButton, Modal} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {ModalCloseBlock, ModalContent} from "views/components/modals/common";
import {Exist} from "views/components/exist/exist.component";
import styled from "@emotion/styled";

import {NavLink} from "react-router-dom";
import {LevelBadge} from "views/components/level_badge/level_badge.component";
import {useLocation} from "react-router";
import Confetti from "react-confetti";
import {useWindowSize} from "data/hooks/useWindowSize";

const Content = styled.div`
	width: 100%;
	display: flex;
	flex-flow: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	p {
		color: #ffffff;
		text-align: center;
		font-size: 16px;
		font-style: normal;
		font-weight: 400;
		line-height: 140%; /* 22.4px */

		b {
			font-weight: bold;
		}
	}

	button {
		max-width: unset;
		margin-top: 16px;
	}
`;

const Title = styled.h4`
	color: #ffffff;
	text-align: center;
	font-size: 20px;
	font-style: normal;
	font-weight: 600;
	line-height: 140%; /* 28px */
`;

export const ModalPromoted: React.FC = observer(() => {
	const location = useLocation();
	const {width, height} = useWindowSize();
	const {i18n, isOpen, isPromoted, isDemoted, points, level, levelStatus, close, confettiState} =
		useViewController<IModalPromotedController>(Bindings.ModalPromotedController, {
			location,
		});

	const promotedTranslation = i18n.t(
		"modal.promotion.promoted_text",
		"Congratulations on scoring <br /> <b>{{X}}</b> Weekly Points",
		{X: points}
	);
	const demotedTranslation = i18n.t(
		"modal.promotion.demoted_text",
		"You scored <b>{{X}}</b> Weekly Points",
		{X: points}
	);

	return (
		<Modal open={isOpen}>
			<Fragment>
				<ModalContent>
					<ModalCloseBlock>
						<IconButton sx={{minWidth: 0}} onClick={() => void close()}>
							<CloseIcon color={"inherit"} />
						</IconButton>
					</ModalCloseBlock>
					<Title
						// Disabled due as Loco - is safe place to render HTML
						/* eslint-disable react/no-danger */
						dangerouslySetInnerHTML={{__html: i18n.t(`league.level.${level}`, level)}}
					/>
					<Content>
						<Exist when={isPromoted}>
							<LevelBadge level={level} size={92} status={levelStatus} />
							<Title>
								{i18n.t(
									"modal.promotion.promoted_title",
									"You have been promoted!"
								)}
							</Title>
							<p
								// Disabled due as Loco - is safe place to render HTML
								/* eslint-disable react/no-danger */
								dangerouslySetInnerHTML={{__html: promotedTranslation}}
							/>
						</Exist>
						<Exist when={isDemoted}>
							<LevelBadge level={level} size={92} status={levelStatus} />
							<Title>
								{i18n.t("modal.promotion.demoted_title", "You have been demoted")}
							</Title>
							<p
								// Disabled due as Loco - is safe place to render HTML
								/* eslint-disable react/no-danger */
								dangerouslySetInnerHTML={{__html: demotedTranslation}}
							/>
						</Exist>
						<NavLink to="/leagues" onClick={() => void close()}>
							<Button fullWidth variant="contained" color="secondary">
								{i18n.t("modal.promotion.action", "View League")}
							</Button>
						</NavLink>
					</Content>
				</ModalContent>
				<Exist when={isPromoted}>
					<Confetti
						width={width}
						height={height}
						colors={["#99FE88", "#fff", "#21CFDC", "#000"]}
						numberOfPieces={300}
						opacity={0.8}
						recycle={confettiState}
					/>
				</Exist>
			</Fragment>
		</Modal>
	);
});
